import React, { useState, useEffect, useCallback } from "react";
import * as api from "../../../api/cabinet";
import { useAuth } from "../../../context/AuthContext";
import BlobCanvas from "../BlobCanvas";
import CabIcon, { PlaceMedal } from "../CabIcon";
import UserAvatar from "../UserAvatar";
import { Button, EmptyState, Skeleton } from "../../ui";
import {
  formatMonthYear,
  daysToMonthEnd,
  pluralDays,
} from "../cabinetUtils";
import "../../../styles/cabinet-top.css";

/**
 * TopScreen → «Битва рейтингов». Порт TopPlayersScreen.kt, 1-в-1.
 * hero → призовой блок → «как заработать» → текущий лидерборд +
 * «моё место» → зал славы (архив).
 */
const LEADERBOARD_PAGE = 10;

/* склонение «игроков/игрока/игроки» */
function pluralPlayers(n) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "игрок";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "игрока";
  return "игроков";
}

export default function TopScreen() {
  const { isAuthenticated } = useAuth();
  const [current, setCurrent] = useState(null); // null=loading
  const [myPlace, setMyPlace] = useState(0);
  const [myRating, setMyRating] = useState(0);
  const [archive, setArchive] = useState(null);
  const [error, setError] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(LEADERBOARD_PAGE);

  /* Загрузка лидерборда вынесена отдельно, чтобы кнопка «Повторить»
     перезапрашивала ТОЛЬКО его. Архив грузится своим запросом и при сбое
     лидерборда остаётся на экране — перезагружать страницу целиком ради
     одного упавшего запроса незачем. */
  const loadCurrent = useCallback((signal) => {
    setError(null);
    return api
      .getBattleRatingCurrent(signal)
      .then((res) => {
        setCurrent(res.data);
        setMyPlace(res.myPlace);
        setMyRating(res.myRating);
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setError(e.message || "Ошибка загрузки лидерборда");
        setCurrent([]);
      });
  }, []);

  const reload = () => {
    setReloading(true);
    setCurrent(null);          // вернуть скелетон, а не держать пустоту
    loadCurrent().finally(() => setReloading(false));
  };

  useEffect(() => {
    const ctrl = new AbortController();

    loadCurrent(ctrl.signal);

    api
      .getBattleRatingArchive(12, ctrl.signal)
      .then(setArchive)
      .catch(() => setArchive([]));

    return () => ctrl.abort();
  }, [loadCurrent]);

  const daysLeft = daysToMonthEnd();
  const maxRating =
    current && current.length
      ? Math.max(...current.map((p) => p.rating || 0), 1)
      : 1;

  return (
    <div className="rb-cab-screen rb-top">
      {/* ── hero ───────────────────────────────────────────────── */}
      <div className="rb-top__hero">
        <BlobCanvas className="rb-top__hero-canvas" />
        <div className="rb-top__hero-inner">
          <span className="rb-top__hero-badge">
            До итогов: {daysLeft} {pluralDays(daysLeft)}
          </span>
          <h1 className="rb-top__hero-title">
            <CabIcon name="swords" size={28} /> Битва рейтингов
          </h1>
          <p className="rb-top__hero-sub">
            Соревнование за алмазы.
            <br />
            Каждый месяц — новый раунд.
          </p>
        </div>
      </div>

      {/* ── моё место (сразу под hero) ─────────────────────────── */}
      {isAuthenticated && current !== null && (
        <MyPlaceBanner
          place={myPlace}
          rating={myRating}
          total={Math.max(current?.length || 0, myPlace)}
        />
      )}

      {/* ── призовой блок ──────────────────────────────────────── */}
      <div className="rb-top__prizes">
        <span className="rb-top__prizes-badge">
          <CabIcon name="diamond" size={13} /> ПРИЗОВОЙ ФОНД
        </span>
        <h2 className="rb-top__prizes-title">Призы топ-3 этого месяца</h2>
        <p className="rb-top__prizes-text">
          Решай задачи и попадай в топ — алмазы автоматически зачислятся 1-го
          числа.
        </p>
        <div className="rb-top__prizes-row">
          <PrizeCard place={1} diamonds={100} tone="gold" pulse />
          <PrizeCard place={2} diamonds={50} tone="silver" />
          <PrizeCard place={3} diamonds={20} tone="bronze" />
        </div>
      </div>

      {/* ── как заработать рейтинг ─────────────────────────────── */}
      <div className="rb-top__earn">
        <h2 className="rb-top__earn-title">
          <CabIcon name="target" size={19} /> Как зарабатывать рейтинг
        </h2>
        <p className="rb-top__earn-sub">
          Рейтинг каждого 1-го числа обнуляется. Чем выше место в конце месяца —
          тем больше алмазов.
        </p>
        <EarnCard
          icon="trophy"
          title="Лидерборды игр"
          text="Попадай в топ времени или очков любой игры на платформе — за это начисляется рейтинг."
          tone="gold"
        />
        <EarnCard
          icon="book"
          title="Задачи в Майнкрафтии"
          text="Решай задачи на minecraftia-school.ru — автопроверка сразу даёт рейтинг и алмазы."
          tone="blue"
        />
        <EarnCard
          icon="sparkles"
          title="1000+ задач бесплатно"
          text="На сайте школы открыто более тысячи заданий по Python, JavaScript и блокам — без подписки."
          tone="green"
          highlight
        />
      </div>

      {/* ── текущий лидерборд ──────────────────────────────────── */}
      <SectionTitle
        icon="chart"
        title="Текущий лидерборд"
        subtitle="Топ-100 этого месяца. Подведение итогов 1-го числа в 00:00 МСК."
      />

      {current === null ? (
        <LeaderboardSkeleton />
      ) : error && current.length === 0 ? (
        /* Ошибка с выходом. Раньше это была строка текста без действия:
           человек видел «Ошибка загрузки» и мог только перезагрузить
           страницу целиком — вместе с архивом, который загрузился нормально. */
        <EmptyState
          icon={<CabIcon name="warning" size={40} />}
          title="Не получилось загрузить лидерборд"
          desc="Проверь соединение и попробуй ещё раз."
          action={
            <Button variant="primary" onClick={reload} loading={reloading}>
              Повторить
            </Button>
          }
        />
      ) : current.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="target" size={40} />}
          title="Битва только началась"
          desc="Никто ещё не набрал рейтинг в этом месяце. Реши первые задачи — и возглавь таблицу."
          action={
            <Button
              variant="primary"
              onClick={() => window.open("https://minecraftia-school.ru", "_blank", "noopener")}
            >
              Решать задачи
            </Button>
          }
        />
      ) : (
        <>
          <div className="rb-top__leaderboard">
            {current.slice(0, visibleCount).map((p, i) => (
              <LeaderRow
                key={p.userId || i}
                player={p}
                maxRating={maxRating}
                showDivider={i < Math.min(visibleCount, current.length) - 1}
                delay={Math.min(i * 30, 600)}
              />
            ))}
          </div>
          {visibleCount < current.length && (
            <div className="rb-top__loadmore">
              <Button
                variant="ghost"
                block
                onClick={() =>
                  setVisibleCount((v) =>
                    Math.min(v + LEADERBOARD_PAGE, current.length)
                  )
                }
              >
                Показать ещё {current.length - visibleCount}{" "}
                {pluralPlayers(current.length - visibleCount)}
              </Button>
            </div>
          )}
        </>
      )}

      {/* ── зал славы ──────────────────────────────────────────── */}
      <SectionTitle
        icon="museum"
        title="Зал славы"
        subtitle="Победители прошлых месяцев. Это навсегда — не сбрасывается."
      />
      {archive === null ? (
        <div className="rb-top__archive" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} h={128} r="var(--r-lg)" />
          ))}
        </div>
      ) : archive.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="museum" size={40} />}
          title="Зал славы пока пуст"
          desc="Первые победители появятся, когда закончится этот месяц."
        />
      ) : (
        <div className="rb-top__archive">
          {archive.map((entry, i) => (
            <ArchiveCard key={entry.id || i} entry={entry} delay={Math.min(i * 60, 500)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── баннер «моё место» наверху страницы ───────────────────────── */
function MyPlaceBanner({ place, rating, total }) {
  // нет рейтинга — призыв решать задачи
  if (rating <= 0 || place <= 0) {
    return (
      <div className="rb-top__myplace rb-top__myplace--empty">
        <span className="rb-top__myplace-ico rb-top__myplace-ico--soft">
          <CabIcon name="target" size={26} />
        </span>
        <div className="rb-top__myplace-info">
          <div className="rb-top__myplace-title">
            Ты пока не в рейтинге
          </div>
          <div className="rb-top__myplace-sub">
            В битве участвуют только игроки с ненулевым рейтингом. Чтобы
            заработать рейтинг — перейди в Майнкрафтию и реши первые задачи.
          </div>
          <a
            className="rb-top__myplace-cta"
            href="https://minecraftia-school.ru"
            target="_blank"
            rel="noreferrer"
          >
            <CabIcon name="rocket" size={15} />
            Решать задачи
          </a>
        </div>
      </div>
    );
  }
  // есть место в рейтинге
  return (
    <div className="rb-top__myplace">
      <span className="rb-top__myplace-ico">
        <CabIcon name="target" size={26} />
      </span>
      <div className="rb-top__myplace-info">
        <div className="rb-top__myplace-title">
          Твоё место — №{place} из {total}
        </div>
        <div className="rb-top__myplace-sub">Текущий рейтинг: {rating}</div>
      </div>
      {place <= 3 && (
        <span className="rb-top__myplace-medal">
          <PlaceMedal place={place} size={30} />
        </span>
      )}
    </div>
  );
}

/* ── призовая карточка ─────────────────────────────────────────── */
function PrizeCard({ place, diamonds, tone, pulse }) {
  return (
    <div className={`rb-top__prize rb-top__prize--${tone} ${pulse ? "rb-top__prize--pulse" : ""}`}>
      <span className="rb-top__prize-medal">
        <PlaceMedal place={place} size={36} />
      </span>
      <span className="rb-top__prize-place">{place} место</span>
      <span className="rb-top__prize-diamonds">
        <CabIcon name="diamond" size={18} /> {diamonds}
      </span>
    </div>
  );
}

/* ── карточка «как заработать» ─────────────────────────────────── */
function EarnCard({ icon, title, text, tone, highlight }) {
  return (
    <div
      className={`rb-top__earn-card rb-top__earn-card--${tone} ${
        highlight ? "rb-top__earn-card--hl" : ""
      }`}
    >
      <span className="rb-top__earn-ico">
        <CabIcon name={icon} size={24} />
      </span>
      <div className="rb-top__earn-body">
        <div className="rb-top__earn-card-title">
          {title}
          {highlight && <span className="rb-top__earn-free">БЕСПЛАТНО</span>}
        </div>
        <div className="rb-top__earn-card-text">{text}</div>
      </div>
    </div>
  );
}

/* ── строка лидерборда ─────────────────────────────────────────── */
function LeaderRow({ player, maxRating, showDivider, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const name =
    `${player.firstName || ""} ${player.lastName || ""}`.trim() ||
    `id ${player.userId}`;
  const ratio = Math.min((player.rating || 0) / maxRating, 1);
  const isTop3 = player.place <= 3;
  const avatar = api.avatarSrc(player.photo_thumb_b64);

  return (
    <div
      className={`rb-top__lrow-wrap ${visible ? "is-visible" : ""}`}
    >
      <div className="rb-top__lrow">
        <span className="rb-top__lrow-place">
          <PlaceMedal place={player.place} size={20} />
        </span>
        <UserAvatar name={name} photo={avatar} size={38} />
        <span className="rb-top__lrow-name">{name}</span>
        <div className="rb-top__lrow-bar">
          <div
            className={`rb-top__lrow-bar-fill ${isTop3 ? "rb-top__lrow-bar-fill--gold" : ""}`}
            style={{ width: visible ? `${ratio * 100}%` : "0%" }}
          />
        </div>
        <span className="rb-top__lrow-rating">
          <CabIcon name="trophy" size={13} /> {player.rating}
        </span>
      </div>
      {showDivider && <div className="rb-top__lrow-divider" />}
    </div>
  );
}

/* ── карточка архива ───────────────────────────────────────────── */
function ArchiveCard({ entry, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div className={`rb-top__arch ${visible ? "is-visible" : ""}`}>
      <div className="rb-top__arch-date">
        <CabIcon name="calendar" size={14} /> {formatMonthYear(entry.date)}
      </div>
      <div className="rb-top__arch-podium">
        <PodiumRow rank={1} place={entry.first} />
        <PodiumRow rank={2} place={entry.second} />
        <PodiumRow rank={3} place={entry.third} />
      </div>
    </div>
  );
}

function PodiumRow({ rank, place }) {
  if (!place) {
    return (
      <div className="rb-top__podium-row">
        <span className="rb-top__podium-medal">
          <PlaceMedal place={rank} size={18} />
        </span>
        <span className="rb-top__podium-empty">—</span>
      </div>
    );
  }
  const name =
    `${place.firstName || ""} ${place.lastName || ""}`.trim() ||
    `id ${place.userId}`;
  const avatar = api.avatarSrc(place.photo_thumb_b64);
  return (
    <div className="rb-top__podium-row">
      <span className="rb-top__podium-medal">
        <PlaceMedal place={rank} size={18} />
      </span>
      <UserAvatar name={name} photo={avatar} size={28} />
      <span className="rb-top__podium-name">{name}</span>
    </div>
  );
}

/* ── общие ─────────────────────────────────────────────────────── */
function SectionTitle({ icon, title, subtitle }) {
  return (
    <div className="rb-top__sectitle">
      <div className="rb-top__sectitle-title">
        <CabIcon name={icon} size={18} /> {title}
      </div>
      <div className="rb-top__sectitle-sub">{subtitle}</div>
    </div>
  );
}

/**
 * Скелетон лидерборда — строки той же формы, что и настоящие.
 *
 * Раньше здесь крутился спиннер с подписью «Загружаем лидерборд...».
 * Форма контента известна заранее (10 строк: место, аватар, ник, полоса,
 * рейтинг), поэтому скелетон честнее: он держит высоту, и при появлении
 * данных страница не подпрыгивает на пол-экрана.
 */
function LeaderboardSkeleton({ rows = 6 }) {
  return (
    <div className="rb-top__leaderboard" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rb-top__lrow rb-top__lrow--skeleton">
          <Skeleton w={26} h={26} r="50%" />
          <Skeleton w={38} h={38} r="50%" />
          <div className="rb-top__lrow-sk-body">
            <Skeleton w="42%" h={14} />
            <Skeleton w="70%" h={6} r={999} />
          </div>
          <Skeleton w={54} h={18} />
        </div>
      ))}
    </div>
  );
}
